import { useEffect, useState, useCallback } from "react";
import { getAllBookings, updateBookingStatus, updateBookingNotes, getInquiry } from "../eventsApi";
import type { CallBooking, CallBookingStatus, Inquiry } from "../types";
import { EVENT_TYPE_LABELS, STATUS_LABELS } from "../types";

type Tab = "upcoming" | "past" | "cancelled";

const STATUS_BADGE: Record<CallBookingStatus, { label: string; cls: string }> = {
  scheduled: { label: "Scheduled", cls: "bg-blue-500/20 text-blue-300" },
  completed: { label: "Completed", cls: "bg-green-500/20 text-green-300" },
  cancelled: { label: "Cancelled", cls: "bg-red-500/20 text-red-300" },
  no_show: { label: "No Show", cls: "bg-yellow-500/20 text-yellow-300" },
};

export function ScheduledCallsView() {
  const [bookings, setBookings] = useState<CallBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("upcoming");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [toast, setToast] = useState("");
  const [inquiry, setInquiry] = useState<Inquiry | null>(null);
  const [inquiryLoading, setInquiryLoading] = useState(false);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await getAllBookings();
      setBookings(res.data);
    } catch (err) {
      console.error("Failed to load bookings", err);
      setLoadError("Couldn't load scheduled calls.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2000);
  };

  const now = new Date();
  const filtered = bookings.filter((b) => {
    if (tab === "upcoming") return b.status === "scheduled" && new Date(b.scheduled_at) >= now;
    if (tab === "past")
      return (
        b.status === "completed" ||
        b.status === "no_show" ||
        (b.status === "scheduled" && new Date(b.scheduled_at) < now)
      );
    return b.status === "cancelled";
  });

  const selected = bookings.find((b) => b.id === selectedId) || null;

  // Bookings made before the widget started passing `call_booking_id` through
  // to inquiry creation have no link, so this stays null for them.
  const linkedInquiryId = selected?.inquiry_id ?? null;

  useEffect(() => {
    if (!linkedInquiryId) {
      setInquiry(null);
      return;
    }
    let cancelled = false;
    setInquiryLoading(true);
    getInquiry(linkedInquiryId)
      .then((res) => {
        if (!cancelled) setInquiry(res.data);
      })
      .catch((err) => {
        console.error("Failed to load linked inquiry", err);
        if (!cancelled) setInquiry(null);
      })
      .finally(() => {
        if (!cancelled) setInquiryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [linkedInquiryId]);

  const handleSelect = (b: CallBooking) => {
    setSelectedId(b.id);
    setNotes(b.notes || "");
  };

  const handleSaveNotes = async () => {
    if (!selectedId) return;
    setSavingNotes(true);
    try {
      await updateBookingNotes(selectedId, notes);
      setBookings((prev) => prev.map((b) => (b.id === selectedId ? { ...b, notes } : b)));
      showToast("Notes saved");
    } catch (err) {
      console.error("Failed to save notes", err);
      showToast("Save failed");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleStatusChange = async (id: string, status: CallBookingStatus) => {
    try {
      await updateBookingStatus(id, status);
      setBookings((prev) =>
        prev.map((b) =>
          b.id === id
            ? {
                ...b,
                status,
                cancelled_at: status === "cancelled" ? new Date().toISOString() : b.cancelled_at,
              }
            : b
        )
      );
      showToast(
        status === "completed" ? "Marked complete" : status === "cancelled" ? "Cancelled" : "Updated"
      );
    } catch (err) {
      console.error("Failed to update status", err);
      showToast("Update failed");
    }
  };

  const formatDateTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-tribal-muted">
        Loading scheduled calls…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-sm text-tribal-muted">
        <p>{loadError}</p>
        <button
          type="button"
          onClick={loadBookings}
          className="rounded bg-tribal-red/20 px-3 py-1 text-xs text-tribal-red hover:bg-tribal-red/30"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {toast && (
        <div className="fixed right-4 top-4 z-50 rounded-md bg-green-600/90 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <div className="flex w-full max-w-md flex-col border-r border-tribal-border">
        <div className="flex border-b border-tribal-border bg-tribal-surface">
          {(
            [
              { key: "upcoming", label: "Upcoming" },
              { key: "past", label: "Past" },
              { key: "cancelled", label: "Cancelled" },
            ] as const
          ).map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                tab === t.key
                  ? "border-b-2 border-tribal-red text-white"
                  : "text-tribal-muted hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="p-4 text-sm italic text-tribal-muted">No calls in this category.</p>
          ) : (
            filtered.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => handleSelect(b)}
                className={`w-full border-b border-tribal-border px-4 py-3 text-left transition-colors ${
                  selectedId === b.id
                    ? "border-l-2 border-l-tribal-red bg-tribal-red/10"
                    : "hover:bg-white/5"
                }`}
              >
                <div className="mb-1 flex items-center justify-between">
                  <span className="truncate text-sm font-medium text-white">{b.guest_name}</span>
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                      STATUS_BADGE[b.status]?.cls || "text-tribal-muted"
                    }`}
                  >
                    {STATUS_BADGE[b.status]?.label || b.status}
                  </span>
                </div>
                <p className="text-xs text-tribal-muted">{formatDateTime(b.scheduled_at)}</p>
                {b.notes && (
                  <p className="mt-1 truncate text-xs italic text-tribal-muted">{b.notes}</p>
                )}
              </button>
            ))
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {!selected ? (
          <div className="flex h-full items-center justify-center text-sm text-tribal-muted">
            Select a call to view details
          </div>
        ) : (
          <div className="max-w-xl p-6">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white">{selected.guest_name}</h2>
                <p className="mt-0.5 text-sm text-tribal-muted">
                  {formatDateTime(selected.scheduled_at)} · 15 minutes
                </p>
              </div>
              <span
                className={`rounded-full px-2 py-1 text-xs font-medium ${
                  STATUS_BADGE[selected.status]?.cls || "text-tribal-muted"
                }`}
              >
                {STATUS_BADGE[selected.status]?.label || selected.status}
              </span>
            </div>

            <section className="mb-6 rounded-lg border border-tribal-border bg-tribal-surface p-4">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-tribal-muted">
                Contact
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-tribal-muted">Email</span>
                  <a
                    href={`mailto:${selected.guest_email}`}
                    className="text-tribal-red hover:underline"
                  >
                    {selected.guest_email}
                  </a>
                </div>
                {selected.guest_phone && (
                  <div className="flex justify-between">
                    <span className="text-tribal-muted">Phone</span>
                    <a href={`tel:${selected.guest_phone}`} className="text-white">
                      {selected.guest_phone}
                    </a>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-tribal-muted">Booked</span>
                  <span className="text-white">
                    {new Date(selected.created_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                </div>
              </div>
            </section>

            <section className="mb-6 rounded-lg border border-tribal-border bg-tribal-surface p-4">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-tribal-muted">
                Event inquiry
              </h3>
              {inquiryLoading ? (
                <p className="text-sm text-tribal-muted">Loading inquiry…</p>
              ) : inquiry ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-tribal-muted">Event</span>
                    <span className="text-white">
                      {EVENT_TYPE_LABELS[inquiry.event_type] || inquiry.event_type}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-tribal-muted">Guests</span>
                    <span className="text-white">{inquiry.group_size}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-tribal-muted">Requested</span>
                    <span className="text-white">
                      {inquiry.preferred_dates.length > 0
                        ? inquiry.preferred_dates.map((d) => d.date).join(", ")
                        : "No date given"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-tribal-muted">Pipeline status</span>
                    <span className="text-white">
                      {STATUS_LABELS[inquiry.status] || inquiry.status}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-sm italic text-tribal-muted">
                  Not linked to an inquiry. Match it by guest email in the Pipeline.
                </p>
              )}
            </section>

            <section className="mb-6 rounded-lg border border-tribal-border bg-tribal-surface p-4">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-tribal-muted">
                Notes
              </h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={5}
                placeholder="Add notes about this call…"
                className="w-full resize-none rounded-md border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
              />
              <button
                type="button"
                onClick={handleSaveNotes}
                disabled={savingNotes || notes === (selected.notes || "")}
                className="mt-2 rounded bg-tribal-red/20 px-3 py-1 text-xs text-tribal-red hover:bg-tribal-red/30 disabled:opacity-50"
              >
                {savingNotes ? "Saving…" : "Save Notes"}
              </button>
            </section>

            {selected.status === "scheduled" && (
              <section className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-tribal-muted">
                  Actions
                </h3>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleStatusChange(selected.id, "completed")}
                    className="rounded bg-green-500/20 px-3 py-1.5 text-xs font-medium text-green-300 hover:bg-green-500/30"
                  >
                    Mark Complete
                  </button>
                  <button
                    type="button"
                    onClick={() => handleStatusChange(selected.id, "no_show")}
                    className="rounded bg-yellow-500/20 px-3 py-1.5 text-xs font-medium text-yellow-300 hover:bg-yellow-500/30"
                  >
                    No Show
                  </button>
                  <button
                    type="button"
                    onClick={() => handleStatusChange(selected.id, "cancelled")}
                    className="rounded bg-red-500/20 px-3 py-1.5 text-xs font-medium text-red-300 hover:bg-red-500/30"
                  >
                    Cancel
                  </button>
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
